import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  HiOutlineChatBubbleOvalLeftEllipsis,
  HiOutlineCheck,
  HiOutlineCheckCircle,
  HiOutlinePaperAirplane,
  HiOutlinePhoto,
  HiOutlineXMark,
} from "react-icons/hi2";
import { Spinner } from "../../ui";
import { chatApi } from "../../api/chat";
import { useUser } from "../../hooks";
import { handleFailedRequest, notify, playNotificationSound } from "../../util";
import {
  IChatMessage,
  IResponseError,
  ISendChatMessagePayload,
} from "../../data-access";
import { ChatImage, releaseChatImageCache } from "./chat-image";
import { registerSupportChatOpener } from "./support-chat-control";
import "./support-chat.scss";

const OPEN_POLL_INTERVAL = 5000;
const CLOSED_POLL_INTERVAL = 60000;
const MAX_BODY_LENGTH = 5000;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
// The backend's SendMessageRequest rejects anything above this with a 422
// ("attachment_ids" => "sometimes|array|max:10"), so the widget refuses to
// queue an eleventh screenshot rather than let the send fail.
const MAX_ATTACHMENTS = 10;
const ACCEPTED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];
const LOAD_MORE_SCROLL_THRESHOLD = 48;
// How close to the bottom the reader has to be for new messages to pull the
// thread down. Further up than this and the scroll position is left alone.
const NEAR_BOTTOM_THRESHOLD = 64;

interface IPendingAttachment {
  id: string;
  name: string;
  previewUrl: string;
}

const formatTime = (isoDate: string): string =>
  moment.utc(isoDate).local().format("HH:mm");

const formatDay = (isoDate: string): string => {
  const date = moment.utc(isoDate).local();

  if (date.isSame(moment(), "day")) {
    return "Today";
  }

  if (date.isSame(moment().subtract(1, "day"), "day")) {
    return "Yesterday";
  }

  return date.format("MMM D, YYYY");
};

const isSameDay = (first: string, second: string): boolean =>
  moment.utc(first).local().isSame(moment.utc(second).local(), "day");

/**
 * Folds a response into what is already on screen instead of replacing it.
 * Overlapping responses can then land in any order without losing a message:
 * a page of older history, a poll-triggered reload and a message the user just
 * sent all end up in the same list. Ordered by `(created_at, id)`, the same
 * order the backend pages these tables in - the timestamps are second-precision
 * so the ordered UUID breaks the ties.
 */
const mergeMessagesById = (
  previous: IChatMessage[],
  incoming: IChatMessage[]
): IChatMessage[] => {
  const byId = new Map<string, IChatMessage>();

  previous.forEach((message) => byId.set(message.id, message));
  incoming.forEach((message) => byId.set(message.id, message));

  return Array.from(byId.values()).sort((first, second) => {
    if (first.created_at !== second.created_at) {
      return first.created_at < second.created_at ? -1 : 1;
    }

    return first.id < second.id ? -1 : 1;
  });
};

/**
 * The first message the reader had not seen when the panel opened. Opening it
 * marks the thread read within a second, so this is read off the first response
 * of the session and then held - it marks where the reader came in, not a live
 * state, and it is gone the next time the panel opens.
 */
const findFirstUnreadId = (messages: IChatMessage[]): string | null =>
  messages.find(
    (message) => message.sender_type === "staff" && !message.read_at
  )?.id ?? null;

export const SupportChat = (): JSX.Element | null => {
  const { currentUser } = useUser();
  const userId = currentUser?.id ?? null;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(
    () => document.visibilityState === "visible"
  );
  const [draft, setDraft] = useState("");
  // Frozen at the moment the panel opened. Null once the reader has caught up.
  const [unreadDividerId, setUnreadDividerId] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<
    IPendingAttachment[]
  >([]);

  const threadRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastMessageAtRef = useRef<string | null>(null);
  const unreadCountRef = useRef(0);
  const isOpenRef = useRef(false);
  const loadLatestRef = useRef<() => void>(() => undefined);
  // The first poll only sets the baseline - it must not chime for messages that
  // were already unread when the widget mounted.
  const hasPolledRef = useRef(false);
  const isRestoringScrollRef = useRef(false);
  // The list as it stands right now, so a response that resolves between two
  // renders merges into the current messages rather than a stale closure.
  const messagesRef = useRef<IChatMessage[]>([]);
  // Every loadLatest takes a ticket; only the newest one writes its response.
  const latestRequestRef = useRef(0);
  // False until the first response of the current open session has placed the
  // divider, so the 5s poll cannot move it while the reader is reading.
  const isDividerPlacedRef = useRef(false);
  // True while the reader is at the bottom of the thread. Nothing scrolls the
  // thread for them while this is false.
  const isPinnedToBottomRef = useRef(true);
  // Pending plus in-flight uploads, read synchronously so a burst of files
  // picked in one go cannot slip past the cap.
  const attachmentSlotsRef = useRef(0);
  const userIdRef = useRef<string | null>(userId);

  const isUploading = uploadingCount > 0;
  const attachmentCount = pendingAttachments.length + uploadingCount;

  isOpenRef.current = isOpen;
  unreadCountRef.current = unreadCount;

  const applyMessages = useCallback((next: IChatMessage[]): void => {
    messagesRef.current = next;
    setMessages(next);
  }, []);

  const scrollToBottom = useCallback((): void => {
    const thread = threadRef.current;

    if (thread) {
      thread.scrollTop = thread.scrollHeight;
      isPinnedToBottomRef.current = true;
    }
  }, []);

  const loadLatest = useCallback(
    (options?: { showSpinner?: boolean }): void => {
      latestRequestRef.current += 1;
      const requestId = latestRequestRef.current;
      const showsSpinner = options?.showSpinner === true;

      if (showsSpinner) {
        setIsLoading(true);
      }

      chatApi
        .getConversation()
        .then((response) => {
          if (requestId !== latestRequestRef.current) {
            return;
          }

          const hadMessages = messagesRef.current.length > 0;
          const merged = mergeMessagesById(
            messagesRef.current,
            response.messages
          );

          applyMessages(merged);

          if (!isDividerPlacedRef.current) {
            isDividerPlacedRef.current = true;
            setUnreadDividerId(findFirstUnreadId(merged));
          }

          // has_more describes the window the server just returned, which only
          // describes the whole thread while that window still starts at the
          // oldest message on screen. After paging back it does not, so the
          // value the last loadOlder wrote stays.
          if (!hadMessages || merged[0]?.id === response.messages[0]?.id) {
            setHasMore(response.has_more);
          }

          setUnreadCount(response.conversation.user_unread_count);
          setHasLoadedOnce(true);

          if (!hadMessages || isPinnedToBottomRef.current) {
            window.setTimeout(scrollToBottom, 0);
          }
        })
        .catch((error: IResponseError) => {
          if (requestId !== latestRequestRef.current) {
            return;
          }

          handleFailedRequest(error.status, error.text, error.details);
        })
        .finally(() => {
          // Only the call that raised the spinner lowers it, so a newer request
          // cannot leave the old one's spinner up forever.
          if (showsSpinner) {
            setIsLoading(false);
          }
        });
    },
    [applyMessages, scrollToBottom]
  );

  loadLatestRef.current = () => loadLatest();

  const loadOlder = useCallback((): void => {
    const thread = threadRef.current;
    const oldestMessage = messagesRef.current[0];

    if (!oldestMessage || !hasMore || isLoadingMore || !thread) {
      return;
    }

    const previousScrollHeight = thread.scrollHeight;
    const previousScrollTop = thread.scrollTop;

    isPinnedToBottomRef.current = false;
    setIsLoadingMore(true);

    chatApi
      .getConversation(oldestMessage.id)
      .then((response) => {
        setHasMore(response.has_more);
        applyMessages(
          mergeMessagesById(messagesRef.current, response.messages)
        );
        isRestoringScrollRef.current = true;

        window.setTimeout(() => {
          const currentThread = threadRef.current;

          if (currentThread) {
            currentThread.scrollTop =
              currentThread.scrollHeight -
              previousScrollHeight +
              previousScrollTop;
          }

          isRestoringScrollRef.current = false;
        }, 0);
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsLoadingMore(false));
  }, [applyMessages, hasMore, isLoadingMore]);

  // Keep the poll baseline in sync with whatever is on screen, including the
  // messages we just sent ourselves.
  useEffect(() => {
    if (messages.length) {
      lastMessageAtRef.current = messages[messages.length - 1].created_at;
    }
  }, [messages]);

  // A different account in the same tab must not inherit the previous one's
  // thread, badge or screenshots. The widget sits outside <Routes> and only
  // early-returns on logout, so it never unmounts on its own.
  useEffect(() => {
    if (userIdRef.current === userId) {
      return;
    }

    userIdRef.current = userId;
    // Orphans any response still in flight for the previous account.
    latestRequestRef.current += 1;
    attachmentSlotsRef.current = 0;
    isPinnedToBottomRef.current = true;
    lastMessageAtRef.current = null;
    isDividerPlacedRef.current = false;

    applyMessages([]);
    setIsOpen(false);
    setHasMore(false);
    setUnreadCount(0);
    setUnreadDividerId(null);
    setIsLoading(false);
    setIsLoadingMore(false);
    setHasLoadedOnce(false);
    setDraft("");
    setPendingAttachments((previous) => {
      previous.forEach((attachment) =>
        URL.revokeObjectURL(attachment.previewUrl)
      );

      return [];
    });
    releaseChatImageCache();
  }, [userId, applyMessages]);

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      setIsTabVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Polling: 5s while the panel is open, 60s while it is closed. Keyed on the
  // user id, not the user object - /api/auth/me replaces that object every
  // minute, which would re-arm the interval and fire an extra tick each time.
  useEffect(() => {
    if (!userId) {
      return;
    }

    let isActive = true;

    const tick = (): void => {
      chatApi
        .poll()
        .then((response) => {
          if (!isActive) {
            return;
          }

          const hasNewMessage =
            !!response.last_message_at &&
            response.last_message_at !== lastMessageAtRef.current;
          const hasNewUnread = response.unread_count > unreadCountRef.current;

          setUnreadCount(response.unread_count);

          // Chime on a genuinely new staff reply - but never on the first poll,
          // which just establishes the baseline against existing unread.
          if (hasNewUnread && hasPolledRef.current) {
            playNotificationSound();
          }
          hasPolledRef.current = true;

          if (isOpenRef.current && (hasNewMessage || hasNewUnread)) {
            loadLatestRef.current();
          }
        })
        .catch(() => null);
    };

    tick();
    const intervalId = window.setInterval(
      tick,
      isOpen ? OPEN_POLL_INTERVAL : CLOSED_POLL_INTERVAL
    );

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [userId, isOpen]);

  // Mark staff messages as read while the panel is open and they are on screen.
  // A backgrounded tab is not "on screen", so the badge survives until the user
  // comes back and actually looks at the thread.
  useEffect(() => {
    if (!isOpen || !hasLoadedOnce || !isTabVisible) {
      return;
    }

    const hasUnreadStaffMessage = messages.some(
      (message) => message.sender_type === "staff" && !message.read_at
    );

    if (!hasUnreadStaffMessage && unreadCount === 0) {
      return;
    }

    chatApi
      .markAsRead()
      .then(() => {
        const readAt = new Date().toISOString();

        setUnreadCount(0);
        applyMessages(
          messagesRef.current.map((message) =>
            message.sender_type === "staff" && !message.read_at
              ? { ...message, read_at: readAt }
              : message
          )
        );
      })
      .catch(() => null);
  }, [
    isOpen,
    hasLoadedOnce,
    isTabVisible,
    messages,
    unreadCount,
    applyMessages,
  ]);

  useEffect(() => {
    return () => {
      releaseChatImageCache();
    };
  }, []);

  const openPanel = useCallback((): void => {
    if (isOpenRef.current) {
      return;
    }

    setIsOpen(true);
    // The panel is remounted empty, so opening it always lands on the newest
    // message, whatever the reader was looking at when they closed it.
    isPinnedToBottomRef.current = true;
    // A fresh open re-places the divider off the messages that are still
    // unread right now, which is usually none of them.
    isDividerPlacedRef.current = false;
    setUnreadDividerId(null);
    loadLatest({ showSpinner: !hasLoadedOnce });
    window.setTimeout(() => textareaRef.current?.focus(), 0);
  }, [loadLatest, hasLoadedOnce]);

  const handleToggle = (): void => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      openPanel();
    }
  };

  // Let the sidebar "Share Feedback" link (and anything else) open the widget.
  useEffect(() => registerSupportChatOpener(openPanel), [openPanel]);

  const handleThreadScroll = (): void => {
    const thread = threadRef.current;

    if (!thread || isRestoringScrollRef.current) {
      return;
    }

    isPinnedToBottomRef.current =
      thread.scrollHeight - thread.scrollTop - thread.clientHeight <=
      NEAR_BOTTOM_THRESHOLD;

    if (thread.scrollTop <= LOAD_MORE_SCROLL_THRESHOLD) {
      loadOlder();
    }
  };

  const handleImageLoaded = (): void => {
    // An image growing the thread must not yank a reader who has scrolled up,
    // and must not undo the scroll restoration loadOlder just did.
    if (isRestoringScrollRef.current || !isPinnedToBottomRef.current) {
      return;
    }

    scrollToBottom();
  };

  const uploadFile = (file: File): void => {
    attachmentSlotsRef.current += 1;
    setUploadingCount((current) => current + 1);

    chatApi
      .uploadAttachment(file)
      .then((attachment) => {
        setPendingAttachments((previous) => [
          ...previous,
          {
            id: attachment.id,
            name: attachment.name,
            previewUrl: URL.createObjectURL(file),
          },
        ]);
      })
      .catch((error: IResponseError) => {
        attachmentSlotsRef.current = Math.max(
          attachmentSlotsRef.current - 1,
          0
        );
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() =>
        setUploadingCount((current) => Math.max(current - 1, 0))
      );
  };

  const queueFiles = (files: File[]): void => {
    let hasHitLimit = false;

    files.forEach((file) => {
      if (ACCEPTED_MIME_TYPES.indexOf(file.type) === -1) {
        notify("error", "Only PNG, JPG and WEBP screenshots can be attached.");

        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        notify("error", "The screenshot is too large. The limit is 5 MB.");

        return;
      }

      if (attachmentSlotsRef.current >= MAX_ATTACHMENTS) {
        hasHitLimit = true;

        return;
      }

      uploadFile(file);
    });

    if (hasHitLimit) {
      notify(
        "error",
        `One message can carry up to ${MAX_ATTACHMENTS} screenshots.`
      );
    }
  };

  const handleFilePicked = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const files = event.target.files;

    if (files) {
      queueFiles(Array.from(files));
    }

    event.target.value = "";
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLElement>): void => {
    const items = event.clipboardData?.items;

    if (!items) {
      return;
    }

    const images: File[] = [];

    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];

      if (item.kind === "file" && item.type.indexOf("image/") === 0) {
        const file = item.getAsFile();

        if (file) {
          images.push(file);
        }
      }
    }

    if (images.length) {
      event.preventDefault();
      queueFiles(images);
    }
  };

  const removePendingAttachment = (attachmentId: string): void => {
    setPendingAttachments((previous) =>
      previous.filter((attachment) => {
        if (attachment.id === attachmentId) {
          URL.revokeObjectURL(attachment.previewUrl);
          attachmentSlotsRef.current = Math.max(
            attachmentSlotsRef.current - 1,
            0
          );

          return false;
        }

        return true;
      })
    );
  };

  const handleSend = (): void => {
    const body = draft.trim();

    if (isSending || isUploading || (!body && !pendingAttachments.length)) {
      return;
    }

    const sentAttachments = pendingAttachments;
    const payload: ISendChatMessagePayload = {
      ...(body && { body }),
      ...(sentAttachments.length && {
        attachment_ids: sentAttachments.map((attachment) => attachment.id),
      }),
    };

    setIsSending(true);

    chatApi
      .sendMessage(payload)
      .then((message) => {
        applyMessages(mergeMessagesById(messagesRef.current, [message]));
        setDraft("");
        sentAttachments.forEach((attachment) =>
          URL.revokeObjectURL(attachment.previewUrl)
        );
        setPendingAttachments([]);
        attachmentSlotsRef.current = Math.max(
          attachmentSlotsRef.current - sentAttachments.length,
          0
        );

        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
        }

        window.setTimeout(scrollToBottom, 0);
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsSending(false));
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleDraftChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    setDraft(event.target.value.slice(0, MAX_BODY_LENGTH));

    const textarea = event.target;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  if (!currentUser) {
    return null;
  }

  const canSend =
    !isSending &&
    !isUploading &&
    (!!draft.trim() || pendingAttachments.length > 0);

  // Only the reader's own last message carries a receipt. A column of "Read"
  // labels down the thread is noise.
  const lastUserMessage = [...messages]
    .reverse()
    .find((message) => message.sender_type === "user");

  return (
    <div className="support-chat">
      {isOpen && (
        <div className="support-chat__panel">
          <div className="support-chat__header">
            <div className="support-chat__header-text">
              <span className="support-chat__title">Upwex Support</span>
              <span className="support-chat__subtitle">
                We reply here during working hours
              </span>
            </div>
            <button
              type="button"
              className="support-chat__close"
              aria-label="Close support chat"
              onClick={handleToggle}
            >
              <HiOutlineXMark size={20} />
            </button>
          </div>

          <div
            className="support-chat__thread"
            ref={threadRef}
            onScroll={handleThreadScroll}
          >
            {isLoading && !messages.length && (
              <div className="support-chat__state">
                <Spinner size="small" />
              </div>
            )}

            {isLoadingMore && (
              <div className="support-chat__more">
                <Spinner size="small" />
              </div>
            )}

            {!isLoading && !messages.length && (
              <div className="support-chat__state support-chat__empty">
                <HiOutlineChatBubbleOvalLeftEllipsis size={28} />
                <p>
                  Ask us anything about Upwex. You can attach a screenshot too.
                </p>
              </div>
            )}

            {messages.map((message, index) => {
              const previousMessage = messages[index - 1];
              const showDayDivider =
                !previousMessage ||
                !isSameDay(previousMessage.created_at, message.created_at);
              const isStaff = message.sender_type === "staff";

              return (
                <React.Fragment key={message.id}>
                  {showDayDivider && (
                    <div className="support-chat__day">
                      <span>{formatDay(message.created_at)}</span>
                    </div>
                  )}
                  {message.id === unreadDividerId && (
                    <div className="support-chat__new-divider">
                      <span>New messages</span>
                    </div>
                  )}
                  <div
                    className={`support-chat__message support-chat__message--${
                      isStaff ? "staff" : "user"
                    }`}
                  >
                    <div className="support-chat__bubble">
                      {isStaff && message.sender_name && (
                        <span className="support-chat__sender">
                          {message.sender_name}
                        </span>
                      )}
                      {!!message.body && (
                        <span className="support-chat__body">
                          {message.body}
                        </span>
                      )}
                      {message.attachments.map((attachment) => (
                        <ChatImage
                          key={attachment.id}
                          attachment={attachment}
                          onLoaded={handleImageLoaded}
                        />
                      ))}
                      <span className="support-chat__time">
                        {formatTime(message.created_at)}
                      </span>
                    </div>
                  </div>
                  {message.id === lastUserMessage?.id && (
                    <div className="support-chat__receipt">
                      {message.read_at ? (
                        <>
                          <HiOutlineCheckCircle size={12} />
                          Read
                        </>
                      ) : (
                        <>
                          <HiOutlineCheck size={12} />
                          Sent
                        </>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {(!!pendingAttachments.length || isUploading) && (
            <div className="support-chat__pending">
              {pendingAttachments.map((attachment) => (
                <div
                  className="support-chat__pending-item"
                  key={attachment.id}
                  title={attachment.name}
                >
                  <img src={attachment.previewUrl} alt={attachment.name} />
                  <button
                    type="button"
                    className="support-chat__pending-remove"
                    aria-label={`Remove ${attachment.name}`}
                    onClick={() => removePendingAttachment(attachment.id)}
                  >
                    <HiOutlineXMark size={12} />
                  </button>
                </div>
              ))}
              {isUploading && (
                <div className="support-chat__pending-item support-chat__pending-item--loading">
                  <Spinner size="small" />
                </div>
              )}
            </div>
          )}

          <div className="support-chat__composer" onPaste={handlePaste}>
            <button
              type="button"
              className="support-chat__attach"
              aria-label="Attach a screenshot"
              disabled={attachmentCount >= MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
            >
              <HiOutlinePhoto size={18} />
            </button>
            <input
              ref={fileInputRef}
              className="support-chat__file-input"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              onChange={handleFilePicked}
            />
            <textarea
              ref={textareaRef}
              className="support-chat__textarea"
              placeholder="Write a message, paste a screenshot"
              rows={1}
              value={draft}
              onChange={handleDraftChange}
              onKeyDown={handleKeyDown}
            />
            <button
              type="button"
              className="support-chat__send"
              aria-label="Send message"
              disabled={!canSend}
              onClick={handleSend}
            >
              <HiOutlinePaperAirplane size={18} />
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`support-chat__launcher${
          isOpen ? " support-chat__launcher--open" : ""
        }`}
        aria-label={isOpen ? "Close support chat" : "Open support chat"}
        onClick={handleToggle}
      >
        {isOpen ? (
          <HiOutlineXMark size={24} />
        ) : (
          <HiOutlineChatBubbleOvalLeftEllipsis size={24} />
        )}
        {!isOpen && unreadCount > 0 && (
          <span className="support-chat__badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
    </div>
  );
};
